import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Seite from "../components/Seite";
import Notizzettel from "../components/Notizzettel";

const PRIORITAETEN = ["hoch", "mittel", "niedrig"];
const PRIO_FARBE = { hoch: "#ea4335", mittel: "#fbbc04", niedrig: "#34a853" };

// ─── Sortierbare Unterpunkt-Zeile ─────────────────────────────────────────────
function SortableUnterpunkt({ punkt, onToggle, onLoeschen, onEdit, onNotiz, onVerschiebenOeffnen, verschiebenOffen, andereProjekte, onVerschieben }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: punkt.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const hatNotiz = punkt.notiz && (
    punkt.notiz.text || punkt.notiz.bilder?.length > 0 ||
    punkt.notiz.zeichnung || punkt.notiz.dokumente?.length > 0 || punkt.notiz.links?.length > 0
  );

  return (
    <div ref={setNodeRef} style={{ ...style }}>
      <div style={styles.punkt}>
        <span style={styles.dragHandle} {...attributes} {...listeners}>⠿</span>
        <input type="checkbox" checked={punkt.erledigt || false} onChange={() => onToggle(punkt)} />
        <span style={{ flex: 1, textDecoration: punkt.erledigt ? "line-through" : "none", color: punkt.erledigt ? "#999" : "inherit", fontSize: "0.95rem" }}>
          {punkt.text}
        </span>
        {punkt.erledigtBis && <span style={styles.datum}>📅 {punkt.erledigtBis}</span>}
        {punkt.prioritaet && <span style={{ ...styles.prioBadge, backgroundColor: PRIO_FARBE[punkt.prioritaet] }}>{punkt.prioritaet}</span>}
        <button style={{ ...styles.iconBtn, color: hatNotiz ? "#1a73e8" : "#bbb" }} onClick={() => onNotiz(punkt)} title="Notizzettel">📋</button>
        <button style={styles.iconBtn} onClick={() => onEdit(punkt)} title="Bearbeiten">✏️</button>
        <button style={styles.iconBtn} onClick={() => onVerschiebenOeffnen(punkt)} title="In anderes Projekt verschieben">📦</button>
        <button style={styles.iconBtn} onClick={() => onLoeschen(punkt)} title="Löschen">🗑️</button>
      </div>
      {verschiebenOffen && (
        <div style={styles.verschiebenBox}>
          <select
            style={styles.projektSelect}
            defaultValue=""
            onChange={(e) => e.target.value && onVerschieben(punkt, e.target.value)}
          >
            <option value="" disabled>In Projekt verschieben...</option>
            {andereProjekte.map((p) => <option key={p.id} value={p.id}>{p.titel}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

// ─── Sortierbare Projekt-Karte ────────────────────────────────────────────────
function SortablesProjekt({ projekt, onDelete, alleProjekte }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: projekt.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const sensors = useSensors(useSensor(PointerSensor));

  const [editTitel, setEditTitel] = useState(false);
  const [titelText, setTitelText] = useState(projekt.titel);
  const [neuerPunkt, setNeuerPunkt] = useState("");
  const [neuePrio, setNeuePrio] = useState("mittel");
  const [neuesDatum, setNeuesDatum] = useState("");
  const [editPunkt, setEditPunkt] = useState(null);
  const [notizPunkt, setNotizPunkt] = useState(null);
  const [verschiebenPunktId, setVerschiebenPunktId] = useState(null);

  const andereProjekte = alleProjekte.filter((p) => p.id !== projekt.id);

  const punktVerschieben = async (punkt, zielProjektId) => {
    const zielProjekt = alleProjekte.find((p) => p.id === zielProjektId);
    if (!zielProjekt) return;
    const punkteOhne = (projekt.unterpunkte || []).filter((p) => p.id !== punkt.id);
    const punkteZiel = [...(zielProjekt.unterpunkte || []), punkt];
    await updateDoc(doc(db, "projekte", projekt.id), { unterpunkte: punkteOhne });
    await updateDoc(doc(db, "projekte", zielProjektId), { unterpunkte: punkteZiel });
    setVerschiebenPunktId(null);
  };

  const punktHinzufuegen = async (e) => {
    e.preventDefault();
    if (!neuerPunkt.trim()) return;
    const punkte = [...(projekt.unterpunkte || []), {
      text: neuerPunkt, erledigt: false, id: Date.now().toString(),
      prioritaet: neuePrio, erledigtBis: neuesDatum, notiz: {}
    }];
    await updateDoc(doc(db, "projekte", projekt.id), { unterpunkte: punkte });
    setNeuerPunkt(""); setNeuesDatum("");
  };

  const punktToggle = async (punkt) => {
    const punkte = (projekt.unterpunkte || []).map((p) => p.id === punkt.id ? { ...p, erledigt: !p.erledigt } : p);
    await updateDoc(doc(db, "projekte", projekt.id), { unterpunkte: punkte });
  };

  const punktLoeschen = async (punkt) => {
    const punkte = (projekt.unterpunkte || []).filter((p) => p.id !== punkt.id);
    await updateDoc(doc(db, "projekte", projekt.id), { unterpunkte: punkte });
  };

  const punktSpeichern = async () => {
    const punkte = (projekt.unterpunkte || []).map((p) => p.id === editPunkt.id ? editPunkt : p);
    await updateDoc(doc(db, "projekte", projekt.id), { unterpunkte: punkte });
    setEditPunkt(null);
  };

  const notizSpeichern = async (notizDaten) => {
    const punkte = (projekt.unterpunkte || []).map((p) =>
      p.id === notizPunkt.id ? { ...p, notiz: notizDaten } : p
    );
    await updateDoc(doc(db, "projekte", projekt.id), { unterpunkte: punkte });
    setNotizPunkt((prev) => ({ ...prev, notiz: notizDaten }));
  };

  const titelSpeichern = async () => {
    await updateDoc(doc(db, "projekte", projekt.id), { titel: titelText });
    setEditTitel(false);
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const punkte = projekt.unterpunkte || [];
    const oldIndex = punkte.findIndex((p) => p.id === active.id);
    const newIndex = punkte.findIndex((p) => p.id === over.id);
    const neu = arrayMove(punkte, oldIndex, newIndex);
    await updateDoc(doc(db, "projekte", projekt.id), { unterpunkte: neu });
  };

  return (
    <div ref={setNodeRef} style={{ ...styles.projektKarte, ...style }}>
      {notizPunkt && (
        <Notizzettel
          key={notizPunkt.id}
          titel={notizPunkt.text}
          notiz={notizPunkt.notiz || {}}
          onSpeichern={notizSpeichern}
          onSchliessen={() => setNotizPunkt(null)}
          storagePfad={`projekte/${projekt.id}`}
        />
      )}

      {/* Header */}
      <div style={styles.projektHeader}>
        <span style={styles.dragHandle} {...attributes} {...listeners}>⠿</span>
        {editTitel ? (
          <>
            <input style={{ ...styles.input, flex: 1 }} value={titelText} onChange={(e) => setTitelText(e.target.value)} autoFocus />
            <button style={styles.speichern} onClick={titelSpeichern}>💾</button>
            <button style={styles.abbrechen} onClick={() => setEditTitel(false)}>✕</button>
          </>
        ) : (
          <>
            <h3 style={styles.projektTitel}>{projekt.titel}</h3>
            <button style={styles.iconBtn} onClick={() => { setEditTitel(true); setTitelText(projekt.titel); }}>✏️</button>
            <button style={styles.iconBtn} onClick={() => onDelete(projekt.id)}>🗑️</button>
          </>
        )}
      </div>

      {/* Unterpunkt bearbeiten */}
      {editPunkt && (
        <div style={styles.editPunktForm}>
          <input style={styles.input} value={editPunkt.text} onChange={(e) => setEditPunkt({ ...editPunkt, text: e.target.value })} placeholder="Text..." />
          <select style={styles.select} value={editPunkt.prioritaet || "mittel"} onChange={(e) => setEditPunkt({ ...editPunkt, prioritaet: e.target.value })}>
            {PRIORITAETEN.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input type="date" style={styles.input} value={editPunkt.erledigtBis || ""} onChange={(e) => setEditPunkt({ ...editPunkt, erledigtBis: e.target.value })} />
          <button style={styles.speichern} onClick={punktSpeichern}>💾</button>
          <button style={styles.abbrechen} onClick={() => setEditPunkt(null)}>✕</button>
        </div>
      )}

      {/* Unterpunkte */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={(projekt.unterpunkte || []).map((p) => p.id)} strategy={verticalListSortingStrategy}>
          {(projekt.unterpunkte || []).map((punkt) => (
            <SortableUnterpunkt
              key={punkt.id}
              punkt={punkt}
              onToggle={punktToggle}
              onLoeschen={punktLoeschen}
              onEdit={(p) => setEditPunkt({ ...p })}
              onNotiz={(p) => setNotizPunkt({ ...p })}
              onVerschiebenOeffnen={(p) => setVerschiebenPunktId(verschiebenPunktId === p.id ? null : p.id)}
              verschiebenOffen={verschiebenPunktId === punkt.id}
              andereProjekte={andereProjekte}
              onVerschieben={punktVerschieben}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* Neuer Unterpunkt */}
      <form onSubmit={punktHinzufuegen} style={styles.neuerPunktForm}>
        <input style={styles.input} value={neuerPunkt} onChange={(e) => setNeuerPunkt(e.target.value)} placeholder="Neuer Unterpunkt..." />
        <select style={styles.select} value={neuePrio} onChange={(e) => setNeuePrio(e.target.value)}>
          {PRIORITAETEN.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <input type="date" style={styles.input} value={neuesDatum} onChange={(e) => setNeuesDatum(e.target.value)} title="Erledigt bis" />
        <button style={styles.kleinButton} type="submit">+</button>
      </form>
    </div>
  );
}

// ─── Hauptkomponente ──────────────────────────────────────────────────────────
export default function Projekte({ user }) {
  const [projekte, setProjekte] = useState([]);
  const [neuerTitel, setNeuerTitel] = useState("");
  const sensors = useSensors(useSensor(PointerSensor));

  useEffect(() => {
    const q = query(collection(db, "projekte"), where("uid", "==", user.uid));
    return onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (a.reihenfolge || 0) - (b.reihenfolge || 0));
      setProjekte(data);
    });
  }, [user.uid]);

  const projektHinzufuegen = async (e) => {
    e.preventDefault();
    if (!neuerTitel.trim()) return;
    await addDoc(collection(db, "projekte"), {
      titel: neuerTitel, unterpunkte: [],
      uid: user.uid, reihenfolge: projekte.length, erstellt: new Date()
    });
    setNeuerTitel("");
  };

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = projekte.findIndex((p) => p.id === active.id);
    const newIndex = projekte.findIndex((p) => p.id === over.id);
    const neu = arrayMove(projekte, oldIndex, newIndex);
    setProjekte(neu);
    for (let i = 0; i < neu.length; i++) {
      await updateDoc(doc(db, "projekte", neu[i].id), { reihenfolge: i });
    }
  };

  return (
    <Seite titel="Projekte">
      <form onSubmit={projektHinzufuegen} style={styles.form}>
        <input style={styles.input} value={neuerTitel} onChange={(e) => setNeuerTitel(e.target.value)} placeholder="Neues Projekt..." />
        <button style={styles.button} type="submit">Erstellen</button>
      </form>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={projekte.map((p) => p.id)} strategy={verticalListSortingStrategy}>
          {projekte.map((projekt) => (
            <SortablesProjekt key={projekt.id} projekt={projekt} alleProjekte={projekte} onDelete={(id) => deleteDoc(doc(db, "projekte", id))} />
          ))}
        </SortableContext>
      </DndContext>
      {projekte.length === 0 && <p style={styles.leer}>Noch keine Projekte.</p>}
    </Seite>
  );
}

const styles = {
  form: { display: "flex", gap: "0.5rem", marginBottom: "1.5rem" },
  input: { flex: 1, padding: "0.6rem", fontSize: "0.95rem", border: "1px solid #ccc", borderRadius: "4px", minWidth: "80px" },
  select: { padding: "0.6rem", border: "1px solid #ccc", borderRadius: "4px", fontSize: "0.9rem" },
  button: { padding: "0.6rem 1rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  kleinButton: { padding: "0.4rem 0.8rem", backgroundColor: "#1a73e8", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  projektKarte: { backgroundColor: "white", borderRadius: "8px", padding: "1rem", marginBottom: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.12)" },
  projektHeader: { display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.8rem" },
  projektTitel: { margin: 0, flex: 1, fontSize: "1.05rem" },
  dragHandle: { cursor: "grab", fontSize: "1.2rem", color: "#bbb", userSelect: "none" },
  punkt: { display: "flex", alignItems: "center", gap: "0.4rem", padding: "0.35rem 0", borderBottom: "1px solid #f5f5f5" },
  prioBadge: { fontSize: "0.7rem", color: "white", padding: "0.15rem 0.4rem", borderRadius: "8px" },
  datum: { fontSize: "0.75rem", color: "#666", whiteSpace: "nowrap" },
  iconBtn: { background: "none", border: "none", cursor: "pointer", fontSize: "0.95rem", padding: "0 2px" },
  speichern: { padding: "0.3rem 0.6rem", backgroundColor: "#34a853", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  abbrechen: { padding: "0.3rem 0.6rem", backgroundColor: "#ea4335", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" },
  neuerPunktForm: { display: "flex", gap: "0.4rem", marginTop: "0.8rem", flexWrap: "wrap" },
  editPunktForm: { display: "flex", gap: "0.4rem", marginBottom: "0.6rem", flexWrap: "wrap", backgroundColor: "#f8f9fa", padding: "0.5rem", borderRadius: "4px" },
  verschiebenBox: { padding: "0.4rem 0 0.4rem 1.6rem" },
  projektSelect: { padding: "0.4rem 0.6rem", border: "1px solid #ddd", borderRadius: "4px", fontSize: "0.85rem", width: "100%", maxWidth: "300px" },
  leer: { color: "#999", textAlign: "center" },
};
